/**
 * **The side-change banner** (`projects/turn-presence.md` 2.3): "<SIDE>' TURN" across the board for
 * {@link TURN_BANNER_MS} on its side's gradient, when the picking side (`activation.activeSide`)
 * changes. A unit activating on the side already picking shows nothing (developer, 2026-09-30); the
 * round header's lit wing names the acting unit. It never pans the camera.
 *
 * The side seen at mount is the baseline, so a re-render or a reconnect's full resync never replays
 * it. Not rendered under reduced motion.
 */
import { useEffect, useRef, useState } from "react";
import type { EncounterState } from "@lancer-vtt/domain";
import { prefersReducedMotion } from "../../../lib/prefersReducedMotion";
import { SweepBanner } from "../../../lib/SweepBanner";

export const TURN_BANNER_MS = 1500;

const SIDE_TURN: Record<"pc" | "npc", string> = { pc: "LANCERS' TURN", npc: "HOSTILES' TURN" };

type Banner = { n: number; side: "pc" | "npc" };

export function TurnBanner({ state }: { state: EncounterState }): React.JSX.Element | null {
  const side = state.activation.activeSide;
  const [banner, setBanner] = useState<Banner | null>(null);
  const seen = useRef(side);

  useEffect(() => {
    if (side === seen.current) return;
    seen.current = side;
    if (prefersReducedMotion()) return;
    setBanner((b) => ({ n: (b?.n ?? 0) + 1, side }));
  }, [side]);

  useEffect(() => {
    if (!banner) return;
    const t = setTimeout(() => setBanner(null), TURN_BANNER_MS);
    return () => clearTimeout(t);
  }, [banner]);

  if (!banner) return null;
  return <SweepBanner key={banner.n} className={`combat-turnbanner is-${banner.side}`} testId="turn-banner" text={SIDE_TURN[banner.side]} />;
}
